import { useCallback, useEffect, useRef } from "react"

/**
 * Global singleton: only one video plays at a time.
 * When a new video becomes the active one, the previous is paused.
 */
let activeVideo: HTMLVideoElement | null = null

interface SavedState {
	muted: boolean
	playing: boolean
	time: number
}

/** In-memory playback state store for seamless handoff between screens. */
const savedStates = new Map<string, SavedState>()

function videoKey(video: HTMLVideoElement) {
	return video.currentSrc || video.src
}

function saveState(video: HTMLVideoElement) {
	const key = videoKey(video)
	if (key) {
		savedStates.set(key, {
			muted: video.muted,
			playing: !video.paused,
			time: video.currentTime,
		})
	}
}

function restoreState(video: HTMLVideoElement) {
	const key = videoKey(video)
	const saved = savedStates.get(key)
	if (!saved) {
		return
	}
	savedStates.delete(key)
	if (saved.time > 0) {
		video.currentTime = saved.time
	}
	video.muted = saved.muted
	if (saved.playing) {
		claimPlayback(video)
		video.play().catch(() => {})
	}
}

function claimPlayback(video: HTMLVideoElement) {
	if (activeVideo && activeVideo !== video) {
		saveState(activeVideo)
		activeVideo.pause()
	}
	activeVideo = video
}

function releasePlayback(video: HTMLVideoElement) {
	if (activeVideo === video) {
		saveState(video)
		activeVideo = null
	}
}

/**
 * Hook that returns a ref callback for a <video> element.
 * - Observes viewport intersection (50% visible threshold)
 * - When visible: claims global playback and plays
 * - When not visible: pauses and releases
 * - On manual play (user clicks play): claims global playback
 * - Saves/restores playback position, muted, and playing state across screen transitions
 */
export function useVideoPlayback() {
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const observerRef = useRef<IntersectionObserver | null>(null)

	// Clean up observer + listeners on unmount
	useEffect(() => {
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect()
				observerRef.current = null
			}
			if (videoRef.current) {
				saveState(videoRef.current)
				releasePlayback(videoRef.current)
				videoRef.current = null
			}
		}
	}, [])

	const refCallback = useCallback((node: HTMLVideoElement | null) => {
		// Teardown previous
		if (observerRef.current) {
			observerRef.current.disconnect()
			observerRef.current = null
		}
		if (videoRef.current) {
			videoRef.current.removeEventListener("play", handlePlay)
			saveState(videoRef.current)
			releasePlayback(videoRef.current)
		}

		videoRef.current = node
		if (!node) {
			return
		}

		// Restore saved state once metadata is loaded
		function handleLoadedMetadata() {
			if (videoRef.current) {
				restoreState(videoRef.current)
			}
		}
		node.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true })
		// If metadata is already loaded (cached), restore immediately
		if (node.readyState >= 1) {
			restoreState(node)
		}

		// When user manually clicks play, claim global playback
		function handlePlay() {
			if (videoRef.current) {
				claimPlayback(videoRef.current)
			}
		}
		node.addEventListener("play", handlePlay)

		// Observe intersection for auto play/pause
		const observer = new IntersectionObserver(
			entries => {
				const entry = entries[0]
				if (!entry || !videoRef.current) {
					return
				}

				if (entry.isIntersecting) {
					claimPlayback(videoRef.current)
					videoRef.current.play().catch(() => {})
				} else {
					videoRef.current.pause()
					releasePlayback(videoRef.current)
				}
			},
			{ threshold: 0.5 },
		)
		observer.observe(node)
		observerRef.current = observer
	}, [])

	return refCallback
}
